import Link from '@gik/core/components/Link.tsx';
import type { InkindPageAPIModel } from '@gik/core/models/gik/InkindPage';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { nativeShareSupported } from '@gik/core/utils/browser';
import { formatRelativeTime } from '@gik/core/utils/format';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { InkindPageCardBase } from '@gik/inkind-page/components/InkindPageCard/InkindPageCardBase';
import { openShareInkindModal } from '@gik/inkind-page/components/ShareInkindModal/openShareInkindModal';
import type { InkindAlgoliaObject } from '@gik/search/components';
import { Button } from '@gik/ui-legacy/Button';
import { HoverAnimation } from '@gik/ui-legacy/HoverAnimation';
import ShareAlt from '@gik/ui-legacy/SvgIcon/GikIcons/ShareAlt';
import { SvgIcon } from '@gik/ui-legacy/SvgIcon/SvgIcon';
import { useInkindAPIOrAlgolia } from '@gik/user-profile/hooks/useInkindAPIOrAlgolia';
import type { Hit } from '@algolia/client-search';
import DocumentRemoveIcon from '@heroicons/react/solid/DocumentRemoveIcon';
import React from 'react';
import { confirmRemovePageFromGroup } from './confirmDeactivateGroup';

/** A card renders either an Algolia search hit or a freshly-added API Page. */
export type InkindGroupCardItem = Hit<InkindAlgoliaObject> | InkindPageAPIModel;

export type InkindGroupCardProps = {
  hit: InkindGroupCardItem;
  /** Called when the user clicks the deactivate (trash) button. Omitted when the user can't manage Pages. */
  onDeactivate?(hit: InkindGroupCardItem): void | Promise<void>;
};

const blockName = 'inkind-group-card';

function InkindGroupCardComp({ hit, onDeactivate }: InkindGroupCardProps) {
  const bem = useBemCN(blockName);

  const { slug, title, imgSrc, routeId, location, shortUrl, url } = useInkindAPIOrAlgolia(hit);

  // Present only on Algolia hits (Unix seconds); freshly-added API Pages omit it.
  const modifiedTimestamp = (hit as Hit<InkindAlgoliaObject>).modifiedTimestamp;

  // Respect the Page's sharing setting. Undefined (legacy/un-reindexed hits or
  // freshly-added API Pages) defaults to showing, preserving prior behaviour.
  const allowPageSharing =
    (hit as Hit<InkindAlgoliaObject> & { allowPageSharing?: boolean }).allowPageSharing !== false;

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openShareInkindModal(
      {
        pageTitle: title,
        pageShortUrl: shortUrl,
        inkindRouteId: routeId,
        moreOptionsButton: nativeShareSupported(),
      },
      'profilePage',
      url,
      false
    );
  };

  const handleDeactivate = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!(await confirmRemovePageFromGroup())) return;

    onDeactivate?.(hit);
  };

  return (
    <HoverAnimation {...bem('hover-wrapper')}>
      <div {...bem()}>
        {/* Action buttons float at the top of the image */}
        <section {...bem('actions')}>
          {allowPageSharing && (
            <Button
              size="sm"
              type="button"
              variant="primary"
              onClick={handleShare}
              tooltip="Share Page"
              className="text-neutral-800 flex text-center justify-center"
              circle
            >
              <SvgIcon Icon={ShareAlt} size="xl" name="share" style={{ width: '20px', height: '20px' }} />
            </Button>
          )}
          {/* The remove button only appears when a handler is supplied — i.e. the
              user may manage this group's Pages (backend `groups.update`). */}
          {onDeactivate && (
            <Button
              size="sm"
              type="button"
              variant="danger"
              circle
              onClick={handleDeactivate}
              tooltip="Remove"
              className="ml-auto"
            >
              <div className="text-danger-800 flex text-center justify-center">
                <SvgIcon Icon={DocumentRemoveIcon} name="close" style={{ width: '20px', height: '20px' }} />
              </div>
            </Button>
          )}
        </section>

        <Link href={`/inkinds/${routeId}/${slug}`}>
          <InkindPageCardBase
            imageUrl={imgSrc}
            title={title}
            location={location === ', undefined' ? undefined : location}
            toolbar={
              modifiedTimestamp ? (
                <section {...bem('footer', undefined, 'bg-neutral-100')}>
                  <span {...bem('updated')}>Updated {formatRelativeTime(modifiedTimestamp * 1000)}</span>
                </section>
              ) : undefined
            }
          />
        </Link>
      </div>
    </HoverAnimation>
  );
}

export const InkindGroupCard = withComponentErrorBoundary(InkindGroupCardComp);
